import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { WallpaperService } from '../../services/wallpaper.service';

interface SurfaceOption {
  id?: number;
  label: string;
  key: string;
  standardPrice: number;
  premiumPrice: number;
}

interface ImageInfo {
  width: number;
  height: number;
  aspectRatio: string;
  printWidthFt: number;
  printHeightFt: number;
}

@Component({
  selector: 'app-custom-upload',
  templateUrl: './custom-upload.component.html'
})
export class CustomUploadComponent implements OnInit {
  selectedFile: File | null = null;
  previewUrl: string | null = null;
  isDragging = false;
  imageInfo: ImageInfo | null = null;

  wallWidth: number | null = null;
  wallHeight: number | null = null;
  unit: 'Feet' | 'Inches' | 'Meters' = 'Feet';

  qualityType: 'standard' | 'premium' = 'standard';

  // Default fallback surfaces
  surfaceTypes: SurfaceOption[] = [
    { label: 'Canvas',          key: 'canvas',          standardPrice: 75.65,  premiumPrice: 127.50 },
    { label: 'Premium Canvas',  key: 'premium_canvas',  standardPrice: 146.20, premiumPrice: 210.80 },
    { label: 'Non-Woven',       key: 'non_woven',       standardPrice: 75.65,  premiumPrice: 127.50 },
    { label: 'Self Adhesive',   key: 'self_adhesive',   standardPrice: 70,     premiumPrice: 99 },
    { label: 'Glitter',         key: 'glitter',         standardPrice: 119.85, premiumPrice: 169.15 },
  ];
  selectedSurface: SurfaceOption = this.surfaceTypes[0];

  // Backend price calculation
  calculatedPrice: number | null = null;
  priceLoading = false;

  // Crop tool state
  isCropping = false;
  originalPreviewUrl: string | null = null; // store original before crop
  cropBox = { x: 0, y: 0, w: 0, h: 0 };   // crop selection in % of image
  private cropDragging: 'move' | 'tl' | 'tr' | 'bl' | 'br' | null = null;
  private cropStartMouse = { x: 0, y: 0 };
  private cropStartBox = { x: 0, y: 0, w: 0, h: 0 };
  private boundCropMouseMove = this.onCropMouseMove.bind(this);
  private boundCropMouseUp = this.onCropMouseUp.bind(this);

  // Resize tool state
  isResizing = false;
  resizeWidth: number = 0;
  resizeHeight: number = 0;
  resizeLockAspect = true;
  private resizeOriginalWidth = 0;
  private resizeOriginalHeight = 0;
  private resizeAspectRatio = 1;

  // Upscale tool state
  isUpscaling = false;

  @ViewChild('cropContainer') cropContainer!: ElementRef<HTMLDivElement>;

  constructor(
    public router: Router,
    private wallpaperService: WallpaperService
  ) {}

  ngOnInit() {}

  onFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) { this.selectedFile = file; this.createPreview(file); }
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) { this.selectedFile = file; this.createPreview(file); }
  }

  onDragOver(event: DragEvent) { event.preventDefault(); this.isDragging = true; }
  onDragLeave() { this.isDragging = false; }

  removeFile() {
    this.previewUrl = null;
    this.selectedFile = null;
    this.imageInfo = null;
  }

  selectSurface(surface: SurfaceOption) {
    this.selectedSurface = surface;
    this.calculatedPrice = null;
    if (this.areaInSqFt > 0) {
      this.fetchPrice();
    }
  }

  onQualityChange() {
    this.calculatedPrice = null;
    if (this.areaInSqFt > 0) {
      this.fetchPrice();
    }
  }

  onDimensionChange() {
    this.calculatedPrice = null;
    if (this.areaInSqFt > 0) {
      this.fetchPrice();
    }
  }

  fetchPrice() {
    if (this.areaInSqFt <= 0) return;
    this.priceLoading = true;

    const params: Record<string, any> = {
      width: this.wallWidthFt,
      height: this.wallHeightFt,
      quality: this.qualityType,
    };
    if (this.selectedSurface.id) {
      params['paper_id'] = this.selectedSurface.id;
    } else {
      params['paper'] = this.selectedSurface.key;
    }

    this.wallpaperService.calculateCustomWallpaperPrice(params).subscribe({
      next: (res: any) => {
        const data = res?.data ?? res;
        this.calculatedPrice = data?.price ?? data?.total ?? data?.amount ?? null;
        this.priceLoading = false;
      },
      error: () => {
        // Fallback to local calculation
        this.calculatedPrice = null;
        this.priceLoading = false;
      }
    });
  }

  get currentPricePerSqFt(): number {
    return this.qualityType === 'premium'
      ? this.selectedSurface.premiumPrice
      : this.selectedSurface.standardPrice;
  }

  get wallWidthFt(): number {
    if (!this.wallWidth) return 0;
    if (this.unit === 'Inches') return this.wallWidth / 12;
    if (this.unit === 'Meters') return this.wallWidth * 3.28084;
    return this.wallWidth;
  }

  get wallHeightFt(): number {
    if (!this.wallHeight) return 0;
    if (this.unit === 'Inches') return this.wallHeight / 12;
    if (this.unit === 'Meters') return this.wallHeight * 3.28084;
    return this.wallHeight;
  }

  get areaInSqFt(): number {
    return this.wallWidthFt * this.wallHeightFt;
  }

  get totalPrice(): number {
    if (this.calculatedPrice !== null) return this.calculatedPrice;
    return Math.round(this.areaInSqFt * this.currentPricePerSqFt);
  }

  get isFormValid(): boolean {
    return !!this.selectedFile && !!this.wallWidth && !!this.wallHeight
      && this.wallWidth > 0 && this.wallHeight > 0;
  }

  get effectiveDpi(): number {
    if (!this.imageInfo || !this.wallWidthFt || !this.wallHeightFt) return 0;
    const dpiW = this.imageInfo.width / (this.wallWidthFt * 12);
    const dpiH = this.imageInfo.height / (this.wallHeightFt * 12);
    return Math.round(Math.min(dpiW, dpiH));
  }

  get isLowResolution(): boolean {
    return this.effectiveDpi > 0 && this.effectiveDpi < 150;
  }

  private gcd(a: number, b: number): number {
    return b === 0 ? a : this.gcd(b, a % b);
  }

  private createPreview(file: File) {
    const reader = new FileReader();
    reader.onload = e => {
      this.previewUrl = e.target?.result as string;
      this.updateImageInfo(this.previewUrl!);
    };
    reader.readAsDataURL(file);
  }

  private updateImageInfo(src: string) {
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const g = this.gcd(w, h);
      const printWFt = w / 150 / 12;
      const printHFt = h / 150 / 12;
      this.imageInfo = {
        width: w,
        height: h,
        aspectRatio: `${w / g}:${h / g}`,
        printWidthFt: Math.round(printWFt * 10) / 10,
        printHeightFt: Math.round(printHFt * 10) / 10,
      };
    };
    img.src = src;
  }

  // ── Crop Tool ───────────────────────────────────────────────────────────

  openCrop() {
    if (!this.previewUrl) return;
    this.originalPreviewUrl = this.previewUrl;
    // Default crop selection: centered 60% of the image
    this.cropBox = { x: 20, y: 15, w: 60, h: 70 };
    this.isCropping = true;
  }

  cancelCrop() {
    this.isCropping = false;
    this.previewUrl = this.originalPreviewUrl;
    this.originalPreviewUrl = null;
  }

  applyCrop() {
    if (!this.originalPreviewUrl || !this.imageInfo) return;

    const img = new Image();
    img.onload = () => {
      const sx = Math.round((this.cropBox.x / 100) * img.naturalWidth);
      const sy = Math.round((this.cropBox.y / 100) * img.naturalHeight);
      const sw = Math.round((this.cropBox.w / 100) * img.naturalWidth);
      const sh = Math.round((this.cropBox.h / 100) * img.naturalHeight);

      const canvas = document.createElement('canvas');
      canvas.width = sw;
      canvas.height = sh;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);

      this.previewUrl = canvas.toDataURL('image/jpeg', 0.95);
      this.isCropping = false;
      this.originalPreviewUrl = null;

      // Update image info with cropped dimensions
      this.updateImageInfo(this.previewUrl);

      // Update the selected file to the cropped version
      canvas.toBlob(blob => {
        if (blob) {
          const fileName = this.selectedFile?.name || 'cropped.jpg';
          this.selectedFile = new File([blob], fileName, { type: 'image/jpeg' });
        }
      }, 'image/jpeg', 0.95);
    };
    img.src = this.originalPreviewUrl;
  }

  onCropHandleMouseDown(e: MouseEvent | TouchEvent, handle: 'move' | 'tl' | 'tr' | 'bl' | 'br') {
    e.preventDefault();
    e.stopPropagation();
    this.cropDragging = handle;
    const point = this.getEventPoint(e);
    this.cropStartMouse = { x: point.x, y: point.y };
    this.cropStartBox = { ...this.cropBox };
    document.addEventListener('mousemove', this.boundCropMouseMove);
    document.addEventListener('mouseup', this.boundCropMouseUp);
    document.addEventListener('touchmove', this.boundCropMouseMove, { passive: false });
    document.addEventListener('touchend', this.boundCropMouseUp);
  }

  private onCropMouseMove(e: MouseEvent | TouchEvent) {
    if (!this.cropDragging || !this.cropContainer) return;
    e.preventDefault();

    const container = this.cropContainer.nativeElement;
    const rect = container.getBoundingClientRect();
    const point = this.getEventPoint(e);

    // Delta in percentage of container
    const dx = ((point.x - this.cropStartMouse.x) / rect.width) * 100;
    const dy = ((point.y - this.cropStartMouse.y) / rect.height) * 100;

    const s = this.cropStartBox;
    const minSize = 10; // minimum 10% crop size

    if (this.cropDragging === 'move') {
      let nx = s.x + dx;
      let ny = s.y + dy;
      nx = Math.max(0, Math.min(100 - s.w, nx));
      ny = Math.max(0, Math.min(100 - s.h, ny));
      this.cropBox = { x: nx, y: ny, w: s.w, h: s.h };
    } else if (this.cropDragging === 'tl') {
      let nx = s.x + dx;
      let ny = s.y + dy;
      let nw = s.w - dx;
      let nh = s.h - dy;
      if (nw < minSize) { nx = s.x + s.w - minSize; nw = minSize; }
      if (nh < minSize) { ny = s.y + s.h - minSize; nh = minSize; }
      if (nx < 0) { nw += nx; nx = 0; }
      if (ny < 0) { nh += ny; ny = 0; }
      this.cropBox = { x: nx, y: ny, w: nw, h: nh };
    } else if (this.cropDragging === 'tr') {
      let ny = s.y + dy;
      let nw = s.w + dx;
      let nh = s.h - dy;
      if (nw < minSize) nw = minSize;
      if (nh < minSize) { ny = s.y + s.h - minSize; nh = minSize; }
      if (ny < 0) { nh += ny; ny = 0; }
      if (s.x + nw > 100) nw = 100 - s.x;
      this.cropBox = { x: s.x, y: ny, w: nw, h: nh };
    } else if (this.cropDragging === 'bl') {
      let nx = s.x + dx;
      let nw = s.w - dx;
      let nh = s.h + dy;
      if (nw < minSize) { nx = s.x + s.w - minSize; nw = minSize; }
      if (nh < minSize) nh = minSize;
      if (nx < 0) { nw += nx; nx = 0; }
      if (s.y + nh > 100) nh = 100 - s.y;
      this.cropBox = { x: nx, y: s.y, w: nw, h: nh };
    } else if (this.cropDragging === 'br') {
      let nw = s.w + dx;
      let nh = s.h + dy;
      if (nw < minSize) nw = minSize;
      if (nh < minSize) nh = minSize;
      if (s.x + nw > 100) nw = 100 - s.x;
      if (s.y + nh > 100) nh = 100 - s.y;
      this.cropBox = { x: s.x, y: s.y, w: nw, h: nh };
    }
  }

  private onCropMouseUp() {
    this.cropDragging = null;
    document.removeEventListener('mousemove', this.boundCropMouseMove);
    document.removeEventListener('mouseup', this.boundCropMouseUp);
    document.removeEventListener('touchmove', this.boundCropMouseMove);
    document.removeEventListener('touchend', this.boundCropMouseUp);
  }

  private getEventPoint(e: MouseEvent | TouchEvent): { x: number; y: number } {
    if ('touches' in e && e.touches.length > 0) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    return { x: (e as MouseEvent).clientX, y: (e as MouseEvent).clientY };
  }

  // ── Upscale Tool ─────────────────────────────────────────────────────────

  openUpscale() {
    if (!this.previewUrl || !this.imageInfo) return;
    this.isUpscaling = true;
  }

  cancelUpscale() {
    this.isUpscaling = false;
  }

  get upscaledWidth(): number {
    return (this.imageInfo?.width || 0) * 2;
  }

  get upscaledHeight(): number {
    return (this.imageInfo?.height || 0) * 2;
  }

  applyUpscale() {
    if (!this.previewUrl || !this.imageInfo) return;

    const img = new Image();
    img.onload = () => {
      const newW = img.naturalWidth * 2;
      const newH = img.naturalHeight * 2;
      const canvas = document.createElement('canvas');
      canvas.width = newW;
      canvas.height = newH;
      const ctx = canvas.getContext('2d')!;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, newW, newH);

      this.previewUrl = canvas.toDataURL('image/jpeg', 0.95);
      this.isUpscaling = false;

      this.updateImageInfo(this.previewUrl);

      canvas.toBlob(blob => {
        if (blob) {
          const fileName = this.selectedFile?.name || 'upscaled.jpg';
          this.selectedFile = new File([blob], fileName, { type: 'image/jpeg' });
        }
      }, 'image/jpeg', 0.95);
    };
    img.src = this.previewUrl;
  }

  // ── Resize Tool ─────────────────────────────────────────────────────────

  openResize() {
    if (!this.previewUrl || !this.imageInfo) return;
    this.resizeOriginalWidth = this.imageInfo.width;
    this.resizeOriginalHeight = this.imageInfo.height;
    this.resizeWidth = this.imageInfo.width;
    this.resizeHeight = this.imageInfo.height;
    this.resizeAspectRatio = this.imageInfo.width / this.imageInfo.height;
    this.resizeLockAspect = true;
    this.isResizing = true;
  }

  cancelResize() {
    this.isResizing = false;
  }

  onResizeWidthChange() {
    if (this.resizeLockAspect && this.resizeWidth > 0) {
      this.resizeHeight = Math.round(this.resizeWidth / this.resizeAspectRatio);
    }
  }

  onResizeHeightChange() {
    if (this.resizeLockAspect && this.resizeHeight > 0) {
      this.resizeWidth = Math.round(this.resizeHeight * this.resizeAspectRatio);
    }
  }

  toggleResizeLock() {
    this.resizeLockAspect = !this.resizeLockAspect;
    if (this.resizeLockAspect) {
      // Re-sync height based on current width
      this.resizeAspectRatio = this.resizeWidth / this.resizeHeight;
    }
  }

  get isDownscaling(): boolean {
    return this.resizeWidth <= this.resizeOriginalWidth && this.resizeHeight <= this.resizeOriginalHeight;
  }

  get resizeSizeLabel(): string {
    if (this.resizeWidth === this.resizeOriginalWidth && this.resizeHeight === this.resizeOriginalHeight) {
      return 'No change';
    }
    return this.isDownscaling ? 'downscaling — quality preserved' : 'upscaling — may reduce quality';
  }

  applyResize() {
    if (!this.previewUrl || this.resizeWidth < 1 || this.resizeHeight < 1) return;

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = this.resizeWidth;
      canvas.height = this.resizeHeight;
      const ctx = canvas.getContext('2d')!;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, this.resizeWidth, this.resizeHeight);

      this.previewUrl = canvas.toDataURL('image/jpeg', 0.95);
      this.isResizing = false;

      // Update image info
      this.updateImageInfo(this.previewUrl);

      // Update file
      canvas.toBlob(blob => {
        if (blob) {
          const fileName = this.selectedFile?.name || 'resized.jpg';
          this.selectedFile = new File([blob], fileName, { type: 'image/jpeg' });
        }
      }, 'image/jpeg', 0.95);
    };
    img.src = this.previewUrl;
  }
}
